import { useNavigate, useParams } from '@tanstack/react-router';
import { ArrowUpRight, MessagesSquareIcon, PlusCircle } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { CitationTooltipManager } from '@/components/CitationTooltip';
import type { TemplateChoiceSelection } from '@/components/case-partner/template-choices';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { useCasePartner } from '@/components/contexts/CasePartnerContext';
import { Loading } from '@/components/shared';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import { getTimezone } from '@/components/contexts/TimezoneContext';
import { DocumentSelectorDialog } from '@/components/document-selector';
import { TokenLimitTooltip } from '@/components/shared/TokenLimitTooltip';
import { Button } from '@/components/ui/button';
import { type ChatSession, chatHasVisibleRunningTurn } from '@/hooks/useChat';
import { DOCUMENT_ENRICHMENT_SECTIONS, useDocuments } from '@/hooks/useDocuments';
import { FEATURE_FLAGS, useFeatures } from '@/hooks/useFeatures';
import { useMatterMemory } from '@/hooks/useMatterMemory';
import { useTokenLimit } from '@/hooks/useTokenLimit';
import { apiErrorMessage } from '@/lib/api-client';
import { useOpenBundleId } from '@/lib/bundleRoutes';
import type { DocumentSelection } from '@/types';
import { stripCitationsFromContent, type WebCitationDisplay } from '@/utils/citation-utils';
import { useFocusChatMessage } from './hooks/useFocusChatMessage';
import { freezeStreamingContinuityKey } from '@/utils/streaming-utils';
import { sanitizeUserInput } from '../../src/utils/sanitization';
import type { QuickReplyAnswer } from './components';
import {
  ChatHistorySidebar,
  ChatInputArea,
  DeleteChatDialog,
  buildQuotedPrompt,
  formatAskQuote,
  MessageCitedDocs,
  MessageStream,
  ScrollToBottomButton,
  SelectionAskButton,
} from './components';
import {
  buildLoadedChatSelections,
  createDocumentSelection,
  useAutoOpenProducedDraft,
  useCasePartnerScroll,
} from './hooks';
import { PromptLibrary } from './PromptLibrary';

export const CasePartnerSidePanelContent = () => {
  const navigate = useNavigate();
  const { slug: routeSlug } = useParams({ strict: false });
  const openBundleId = useOpenBundleId();
  const { selectedCase } = useCaseContext();
  const casePartner = useCasePartner();
  const currentChatGuidRef = useRef(casePartner.currentChatGuid);
  currentChatGuidRef.current = casePartner.currentChatGuid;
  const { ui_labels } = useMatterJurisdiction().jurisdiction;
  const { openDocumentPanel } = useSidePanel();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const selectionAskContainerRef = useRef<HTMLDivElement>(null);
  const {
    contentRef,
    isAtBottom,
    isStreamingResponseBeyondFold,
    scrollRef,
    scrollToBottom,
    scrollToResponseStart,
    stopScroll,
    streamingResponseRef,
  } = useCasePartnerScroll(casePartner.isStreamingCurrentChat, {
    guid: casePartner.currentChatGuid,
    isLoading: casePartner.isLoading,
    ready: casePartner.messages.length > 0,
    skip: Boolean(casePartner.focusMessage),
  });

  const slug = routeSlug || casePartner.matterGuid || selectedCase || '';
  const timezone = getTimezone();
  const { tokenLimitInfo } = useTokenLimit();
  const { isEnabled: webResearchEnabled, settings: webResearchSettings } = useFeatures(FEATURE_FLAGS.WEB_RESEARCH);
  const webSearchAvailable = webResearchEnabled && webResearchSettings.configured !== false;
  const { isGenerating: isMatterMemoryGenerating } = useMatterMemory(slug);
  const { documents, fetchedDocuments, isValidating: isDocumentsValidating, mutate } = useDocuments(
    slug,
    undefined,
    undefined,
    undefined,
    {},
    timezone,
    // Case Partner only reads names, token counts and attachments (citations + selections),
    // so skip the enrichment sections to keep this always-on whole-matter fetch cheap.
    { excludeSections: DOCUMENT_ENRICHMENT_SECTIONS },
  );

  useEffect(() => {
    if (!slug) return;
    casePartner.activate(slug);
  }, [slug, casePartner.activate]);

  useEffect(() => {
    if (casePartner.isFullPageOpen) return;
    casePartner.setIsSidebarVisible(false);
  }, [casePartner.isFullPageOpen, casePartner.setIsSidebarVisible]);

  useEffect(() => {
    // Pass undefined while fetch-all is in flight so citations are not marked deleted.
    casePartner.setDocuments(fetchedDocuments);
  }, [fetchedDocuments, casePartner.setDocuments]);

  useEffect(() => {
    casePartner.setDocumentsValidating(isDocumentsValidating);
  }, [isDocumentsValidating, casePartner.setDocumentsValidating]);

  useAutoOpenProducedDraft({
    slug,
    producedDraftDocument: casePartner.latestProducedDraftDocument,
    onConsumed: casePartner.clearLatestProducedDraftDocument,
  });

  const isComposingNewChat = casePartner.isComposingNewChat;
  useFocusChatMessage(
    casePartner.focusMessage,
    !casePartner.isLoading && !isComposingNewChat,
    {
      scrollRef,
      stopScroll,
    },
  );
  const displayedCurrentChatGuid = isComposingNewChat ? null : casePartner.currentChatGuid;
  const streamingContinuityKeyBase = `case-partner:${casePartner.currentChatGuid ?? casePartner.matterGuid ?? 'pending'}`;
  const streamingContinuityKeyRef = useRef(streamingContinuityKeyBase);
  streamingContinuityKeyRef.current = freezeStreamingContinuityKey(
    streamingContinuityKeyRef.current,
    streamingContinuityKeyBase,
    casePartner.isStreamingCurrentChat,
  );
  const streamingContinuityKey = streamingContinuityKeyRef.current;
  const shouldShowHistoryThinkingDot = casePartner.chatHistory.some((chat) => (
    chat.guid !== displayedCurrentChatGuid && chatHasVisibleRunningTurn(chat, casePartner.streamingChatGuid)
  ));

  const restoreLoadedChatState = useCallback((result: {
    documentSelections: DocumentSelection[];
    lastUserWebSearch?: boolean;
  }) => {
    casePartner.setWebSearchEnabled(Boolean(result.lastUserWebSearch));
    casePartner.setDocumentSelections(
      buildLoadedChatSelections(result.documentSelections, documents),
    );
    casePartner.setHasShownInitialSuggestions(true);
  }, [
    documents,
    casePartner.setWebSearchEnabled,
    casePartner.setDocumentSelections,
    casePartner.setHasShownInitialSuggestions,
  ]);

  const renderCitedDocs = useCallback((
    content: string,
    webCitationMeta?: ReadonlyMap<string, WebCitationDisplay>,
  ) => (
    <MessageCitedDocs
      content={content}
      documents={fetchedDocuments}
      slug={slug}
      webCitationMeta={webCitationMeta}
    />
  ), [fetchedDocuments, slug]);

  const handleOpenFullPage = () => {
    if (!slug) return;

    const state = (prev: { hideCasePartnerHistory?: boolean }) => ({
      ...prev,
      hideCasePartnerHistory: true,
    });

    if (casePartner.currentChatGuid) {
      navigate({
        to: '/matter/$slug/case-partner/$chatGuid',
        params: { slug, chatGuid: casePartner.currentChatGuid },
        state,
        resetScroll: false,
      });
      return;
    }

    const selectedDocumentGuids = Array.from(casePartner.documentSelections.keys());

    navigate({
      to: '/matter/$slug/case-partner',
      params: { slug },
      search: selectedDocumentGuids.length > 0
        ? { documents: selectedDocumentGuids.join(',') }
        : {},
      state,
      resetScroll: false,
    });
  };

  const sendCasePartnerMessage = useCallback(async (
    rawInput: string,
    clearComposerInput: boolean,
    userMessageMetadata?: Record<string, unknown>,
  ) => {
    const quote = casePartner.composerQuote;
    const quotedInput = quote
      ? buildQuotedPrompt(quote, rawInput)
      : rawInput;
    if (
      !quotedInput.trim() ||
      (casePartner.isCurrentChatTurnActive && !casePartner.isComposingNewChat)
    ) return;

    const sanitizedInput = sanitizeUserInput(quotedInput);
    if (!sanitizedInput.trim()) {
      if (clearComposerInput) {
        casePartner.setUserInput('');
      }
      return;
    }

    const selectionsForBackend = casePartner.getDocumentSelectionsForBackend();

    if (clearComposerInput) {
      casePartner.setUserInput('');
      casePartner.setComposerQuote(null);
    }
    casePartner.setIsComposingNewChat(false);
    casePartner.setIsSidebarVisible(false);
    scrollToResponseStart();

    const result = await casePartner.sendMessage(sanitizedInput, selectionsForBackend, {
      forceNewChat: casePartner.isComposingNewChat,
      userMessageMetadata: {
        ...userMessageMetadata,
        ...(quote && clearComposerInput
          ? {
              quoted_excerpt: quote,
              quoted_question: rawInput.trim() || undefined,
            }
          : {}),
      },
      webSearch: webSearchAvailable && casePartner.webSearchEnabled,
      ...(openBundleId ? { uiContext: { open_bundle_id: openBundleId } } : {}),
    });
    if (result?.conflict && clearComposerInput) {
      casePartner.setUserInput(rawInput);
      casePartner.setComposerQuote(quote);
    }
  }, [
    casePartner.isCurrentChatTurnActive,
    casePartner.isComposingNewChat,
    casePartner.getDocumentSelectionsForBackend,
    casePartner.setUserInput,
    casePartner.setIsComposingNewChat,
    casePartner.setIsSidebarVisible,
    casePartner.sendMessage,
    casePartner.webSearchEnabled,
    webSearchAvailable,
    casePartner.composerQuote,
    casePartner.setComposerQuote,
    openBundleId,
    scrollToResponseStart,
  ]);

  const handleSendMessage = useCallback(async () => {
    await sendCasePartnerMessage(casePartner.userInput, true);
  }, [casePartner.userInput, sendCasePartnerMessage]);

  const handleQuickReplySubmit = useCallback((value: string, answers: QuickReplyAnswer[]) => {
    void sendCasePartnerMessage(value, false, {
      quick_reply_answers: { answers },
    });
  }, [sendCasePartnerMessage]);

  const handleTemplateChoiceSubmit = useCallback((value: string, selection: TemplateChoiceSelection) => {
    void sendCasePartnerMessage(value, false, {
      template_choice: selection,
    });
  }, [sendCasePartnerMessage]);

  const handleAskAboutSelection = useCallback((text: string) => {
    casePartner.setComposerQuote(formatAskQuote(text));
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [casePartner.setComposerQuote]);

  const handleStartNewChat = useCallback(() => {
    casePartner.setIsSidebarVisible(false);
    casePartner.setRouterQueryDocuments(undefined);
    casePartner.setUserInput('');
    casePartner.setComposerQuote(null);
    casePartner.clearSelections();
    casePartner.setShowAllDocuments(false);
    casePartner.setShowPromptSuggestions(false);
    casePartner.setHasShownInitialSuggestions(false);
    casePartner.startNewChat();
    casePartner.setIsComposingNewChat(true);
    casePartner.resetWebSearchToDefault();
  }, [
    casePartner.startNewChat,
    casePartner.setRouterQueryDocuments,
    casePartner.setUserInput,
    casePartner.clearSelections,
    casePartner.setShowAllDocuments,
    casePartner.setShowPromptSuggestions,
    casePartner.setHasShownInitialSuggestions,
    casePartner.setIsComposingNewChat,
    casePartner.setIsSidebarVisible,
    casePartner.resetWebSearchToDefault,
  ]);

  const handleChatSelection = useCallback(async (chat: ChatSession) => {
    casePartner.setIsComposingNewChat(false);
    if (casePartner.currentChatGuid === chat.guid) {
      casePartner.requestFocusMessage(chat.searchMessageId, casePartner.chatHistorySearchQuery);
      casePartner.setIsSidebarVisible(false);
      return;
    }
    casePartner.setComposerQuote(null);
    casePartner.setRouterQueryDocuments(undefined);
    casePartner.clearSelections();
    casePartner.setShowAllDocuments(false);
    casePartner.setShowPromptSuggestions(false);
    casePartner.setHasShownInitialSuggestions(false);
    const targetGuid = chat.guid;
    try {
      const result = await casePartner.loadChat(targetGuid, { force: true });
      if (!result || currentChatGuidRef.current !== targetGuid) return;
      restoreLoadedChatState(result);
      casePartner.requestFocusMessage(chat.searchMessageId, casePartner.chatHistorySearchQuery);
      casePartner.setIsSidebarVisible(false);
    } catch (error) {
      if (currentChatGuidRef.current !== targetGuid) return;
      toast.error(apiErrorMessage(error) || 'Failed to load chat');
    }
  }, [
    casePartner.chatHistorySearchQuery,
    casePartner.clearSelections,
    casePartner.currentChatGuid,
    casePartner.loadChat,
    casePartner.requestFocusMessage,
    casePartner.setComposerQuote,
    casePartner.setHasShownInitialSuggestions,
    casePartner.setIsComposingNewChat,
    casePartner.setIsSidebarVisible,
    casePartner.setRouterQueryDocuments,
    casePartner.setShowAllDocuments,
    casePartner.setShowPromptSuggestions,
    restoreLoadedChatState,
  ]);

  const handleDeleteChat = useCallback(async (guid: string) => {
    casePartner.closeDeleteDialog();
    if (guid === casePartner.currentChatGuid) {
      casePartner.clearSelections();
      casePartner.setUserInput('');
      casePartner.setComposerQuote(null);
      casePartner.setShowAllDocuments(false);
      casePartner.setShowPromptSuggestions(false);
      casePartner.setHasShownInitialSuggestions(false);
    }

    await casePartner.deleteChat(guid);
  }, [
    casePartner.clearSelections,
    casePartner.closeDeleteDialog,
    casePartner.currentChatGuid,
    casePartner.deleteChat,
    casePartner.setComposerQuote,
    casePartner.setHasShownInitialSuggestions,
    casePartner.setShowAllDocuments,
    casePartner.setShowPromptSuggestions,
    casePartner.setUserInput,
  ]);

  const handleOpenDeleteDialog = useCallback((chatGuid: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    casePartner.openDeleteDialog(chatGuid);
  }, [casePartner.openDeleteDialog]);

  const handleOpenDocument = useCallback((docGuid: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!docGuid) return;

    openDocumentPanel({
      documentGuid: docGuid,
      activeTab: 'document',
    });
  }, [openDocumentPanel]);

  const handleUploadComplete = useCallback(async (uploadedFileNames?: string[]) => {
    if (!uploadedFileNames || uploadedFileNames.length === 0) return;

    try {
      const allDocs = await mutate();

      if (allDocs && allDocs.length > 0) {
        const newlyUploadedDocs = allDocs.filter((doc) => {
          return uploadedFileNames.some((fileName) => {
            const baseName = fileName.split(/[/\\]/).pop() || '';
            return doc.fileName?.includes(baseName);
          });
        });

        if (newlyUploadedDocs.length > 0) {
          casePartner.setDocumentSelections((prev) => {
            const newMap = new Map(prev);

            for (const doc of newlyUploadedDocs) {
              if (!doc.guid || newMap.has(doc.guid)) continue;
              newMap.set(doc.guid, createDocumentSelection(doc));
            }

            return newMap;
          });
        }
      }
    } catch (error) {
      console.error('Error fetching updated documents:', error);
      casePartner.setIsUploading(false);
    }
  }, [
    mutate,
    casePartner.setDocumentSelections,
    casePartner.setIsUploading,
  ]);

  const copyToClipboard = useCallback((content: string, messageId: string | number | undefined) => {
    const msgId = messageId?.toString() || '';
    if (!msgId) return;

    const cleanContent = stripCitationsFromContent(content, true);

    navigator.clipboard.writeText(cleanContent).then(() => {
      casePartner.setCopiedMessageIds((prev) => {
        const updated = new Set(prev);
        updated.add(msgId);
        return updated;
      });

      setTimeout(() => {
        casePartner.setCopiedMessageIds((prev) => {
          const updated = new Set(prev);
          updated.delete(msgId);
          return updated;
        });
      }, 2000);
    }).catch(console.error);
  }, [casePartner.setCopiedMessageIds]);

  return (
    <div className="relative flex h-full flex-col bg-white">
      <DeleteChatDialog
        isOpen={casePartner.isDeleteDialogOpen}
        chatTitle={casePartner.chatHistory.find((chat) => chat.guid === casePartner.chatToDelete)?.title}
        onClose={casePartner.closeDeleteDialog}
        onConfirm={() => {
          if (casePartner.chatToDelete) {
            handleDeleteChat(casePartner.chatToDelete);
          }
        }}
      />
      <ChatHistorySidebar
        isVisible={casePartner.isSidebarVisible}
        onClose={() => casePartner.setIsSidebarVisible(false)}
        chatHistory={casePartner.chatHistory}
        currentChatGuid={displayedCurrentChatGuid}
        streamingChatGuid={casePartner.streamingChatGuid}
        isLoading={casePartner.isLoading && casePartner.chatHistory.length === 0}
        isNewChatDisabled={isComposingNewChat}
        tokenLimitInfo={tokenLimitInfo}
        onNewChat={handleStartNewChat}
        onChatSelect={handleChatSelection}
        onDeleteClick={handleOpenDeleteDialog}
        onRename={casePartner.renameChat}
        hasMore={casePartner.chatHistorySearchQuery ? casePartner.chatHistorySearchHasMore : casePartner.chatHistoryHasMore}
        isLoadingMore={casePartner.isLoadingMoreChatHistory}
        onLoadMore={casePartner.loadMoreChatHistory}
        onSearchQueryChange={casePartner.searchChatHistory}
        remoteSearchQuery={casePartner.chatHistorySearchQuery}
        remoteSearchResults={casePartner.chatHistorySearchResults}
      />

      <div className="flex shrink-0 items-center border-b border-slate-200 bg-slate-50/60 px-3 py-2">
        <div className="flex items-center gap-1">
          {!casePartner.isSidebarVisible && (
          <Button
            variant="ghost"
            size="icon"
            className="relative h-8 w-8 rounded-full text-slate-700 hover:bg-slate-100"
            onClick={() => casePartner.setIsSidebarVisible(true)}
            title="View chat history"
          >
            <span className="relative inline-flex">
              <MessagesSquareIcon className="h-4 w-4" />
              {shouldShowHistoryThinkingDot && (
                <span
                  aria-hidden="true"
                  className="-right-1 -top-1 absolute h-2 w-2 animate-pulse rounded-full bg-[#8FA0D0] ring-2 ring-white"
                />
              )}
            </span>
          </Button>
          )}

          {!casePartner.isSidebarVisible && !isComposingNewChat && casePartner.hasConversationActivity && (
            <TokenLimitTooltip
              message={tokenLimitInfo.message}
              disabled={!tokenLimitInfo.isWithinLimit}
              side="bottom"
            >
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full text-slate-700 hover:bg-slate-100"
                onClick={handleStartNewChat}
                title="Start a new chat"
                disabled={!tokenLimitInfo.isWithinLimit}
              >
                <PlusCircle className="h-4 w-4" />
              </Button>
            </TokenLimitTooltip>
          )}

          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full text-slate-700 hover:bg-slate-100"
            onClick={handleOpenFullPage}
            title="Open full Case Partner"
          >
            <ArrowUpRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <DocumentSelectorDialog
        open={casePartner.showDocumentSelector}
        onOpenChange={casePartner.setShowDocumentSelector}
        matterSlug={slug}
        documentSelections={casePartner.documentSelections}
        onToggleSelection={casePartner.toggleDocumentSelection}
        onClearSelection={casePartner.clearSelections}
        onUploadComplete={handleUploadComplete}
        onDialogClose={() => {
          setTimeout(() => inputRef.current?.focus(), 100);
        }}
        chatInputRef={inputRef}
      />

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <CitationTooltipManager>
          <div
            className="absolute inset-0 overflow-y-auto px-5 pb-5 pt-4 [scrollbar-gutter:stable]"
            ref={(node) => {
              scrollRef.current = node;
              selectionAskContainerRef.current = node;
            }}
          >
            <SelectionAskButton
              containerRef={selectionAskContainerRef}
              onAsk={handleAskAboutSelection}
            />
            <div className="space-y-5" ref={contentRef}>
              {isComposingNewChat && !casePartner.isLoading ? (
                <div className="flex min-h-80 flex-col items-center justify-center px-3 text-center">
                  <div className="mb-4 rounded-full border border-slate-200 bg-slate-50 p-3">
                    <img src="/logo-icon.svg" className="h-9 w-9" alt="Crimson AI" />
                  </div>
                  <h3 className="mb-1 text-base font-semibold text-slate-800">How can I assist?</h3>
                  <p className="max-w-xs text-sm text-slate-500">
                    {ui_labels.case_partner_empty_hint}
                  </p>
                </div>
              ) : casePartner.isLoading && !casePartner.hasConversationActivity ? (
                <div className="flex min-h-80 items-center justify-center">
                  <Loading />
                </div>
              ) : (
                <MessageStream
                  messages={casePartner.messages}
                  streamingContent={casePartner.isStreamingCurrentChat ? casePartner.streamingContent : ''}
                  streamingQuickReplyQuestions={
                    casePartner.isStreamingCurrentChat ? casePartner.streamingQuickReplyQuestions : []
                  }
                  streamingTemplateChoices={
                    casePartner.isStreamingCurrentChat ? casePartner.streamingTemplateChoices : null
                  }
                  streamingThinkingSteps={
                    casePartner.isStreamingCurrentChat ? casePartner.streamingThinkingSteps : []
                  }
                  streamingTextInsertions={
                    casePartner.isStreamingCurrentChat ? casePartner.streamingTextInsertions : []
                  }
                  streamingWaitMarkers={
                    casePartner.isStreamingCurrentChat ? casePartner.streamingWaitMarkers : []
                  }
                  isStreaming={casePartner.isStreamingCurrentChat}
                  catchingUp={
                    casePartner.isStreamingCurrentChat && casePartner.streamCatchingUp
                  }
                  finalAnswerConfirmed={
                    casePartner.isStreamingCurrentChat && casePartner.finalAnswerConfirmed
                  }
                  turnStartedAt={
                    casePartner.isStreamingCurrentChat ? casePartner.turnStartedAt : null
                  }
                  reasoningDurationMs={
                    casePartner.isStreamingCurrentChat ? casePartner.turnWorkedDurationMs : null
                  }
                  isThinking={casePartner.isStreamingCurrentChat && casePartner.isThinking}
                  stopRequested={casePartner.isStreamingCurrentChat && casePartner.stopRequested}
                  showLoading={false}
                  documents={fetchedDocuments}
                  matterGuid={selectedCase || casePartner.matterGuid || undefined}
                  chatGuid={casePartner.currentChatGuid}
                  slug={slug}
                  copiedMessageIds={casePartner.copiedMessageIds}
                  onCopyMessage={copyToClipboard}
                  renderCitedDocs={renderCitedDocs}
                  streamingContinuityKey={streamingContinuityKey}
                  streamingResponseRef={streamingResponseRef}
                  variant="compact"
                  quickReplyDisabled={casePartner.isCurrentChatTurnActive && !isComposingNewChat}
                  onQuickReplySubmit={handleQuickReplySubmit}
                  templateChoiceDisabled={casePartner.isCurrentChatTurnActive && !isComposingNewChat}
                  onTemplateChoiceSubmit={handleTemplateChoiceSubmit}
                />
              )}
            </div>
          </div>

          {!isAtBottom &&
            casePartner.hasConversationActivity &&
            (
              !casePartner.isStreamingCurrentChat ||
              !casePartner.streamingContent ||
              isStreamingResponseBeyondFold
            ) && (
              <ScrollToBottomButton
                className="absolute bottom-3 left-1/2 z-10 -translate-x-1/2"
                compact
                isWorking={casePartner.isStreamingCurrentChat}
                onClick={() => scrollToBottom({ animation: 'smooth' })}
              />
            )}
        </CitationTooltipManager>
      </div>

      <div className="shrink-0 border-t border-slate-200 bg-white/85 px-3 py-3 backdrop-blur">
        <ChatInputArea
          inputRef={inputRef}
          userInput={casePartner.userInput}
          onInputChange={casePartner.setUserInput}
          onSend={handleSendMessage}
          onAbort={casePartner.abortStreaming}
          isStreaming={casePartner.isCurrentChatTurnActive && !isComposingNewChat}
          canAbortStreaming={casePartner.canAbortStreaming}
          isUploading={casePartner.isUploading}
          webSearchEnabled={casePartner.webSearchEnabled}
          onWebSearchChange={casePartner.setWebSearchEnabled}
          showWebSearchToggle={webSearchAvailable}
          tokenLimitInfo={tokenLimitInfo}
          documentSelections={casePartner.documentSelections}
          showAllDocuments={casePartner.showAllDocuments}
          onShowAllDocuments={() => casePartner.setShowAllDocuments(true)}
          onAttachmentClick={() => casePartner.setShowDocumentSelector(true)}
          onOpenDocument={handleOpenDocument}
          onRemoveDocument={casePartner.removeDocumentGroup}
          onToggleParent={casePartner.toggleParentInclusion}
          onToggleAttachment={casePartner.toggleAttachmentInclusion}
          onClearAllDocuments={() => {
            casePartner.clearSelections();
            casePartner.setShowAllDocuments(false);
          }}
          onPromptLibraryClick={() => casePartner.setShowPromptLibrary(true)}
          matterGuid={slug}
          chatGuid={displayedCurrentChatGuid}
          quotedExcerpt={casePartner.composerQuote}
          onClearQuote={() => casePartner.setComposerQuote(null)}
          isMatterMemoryGenerating={isMatterMemoryGenerating}
          variant="conversation"
        />

        <p className="mt-2 text-center text-xs text-slate-400">
          Case Partner can make mistakes. Verify the outputs.
        </p>
      </div>

      <PromptLibrary
        open={casePartner.showPromptLibrary}
        onOpenChange={casePartner.setShowPromptLibrary}
        onSelectPrompt={(prompt) => {
          casePartner.setUserInput(prompt);
          setTimeout(() => inputRef.current?.focus(), 100);
        }}
      />
    </div>
  );
};

export default CasePartnerSidePanelContent;
